/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useState, useEffect } from 'react';
import { isEmail } from 'validator';
import { toast } from 'react-toastify';

import { useLocation, useNavigate, Navigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import * as actions from '../../store/modules/auth/actions';

import Loading from '../../components/Loading';
import loginVisual from '../../assets/login.svg';
import logoMyshop from '../../assets/logo-myshop.png';

import {
   AuthPage,
   AuthContainer,
   AuthGrid,
   AuthVisual,
   AuthCard,
   AuthTitle,
   AuthSubtitle,
   AuthForm,
   AuthActions,
   AuthBrand,
   AuthLogo,
   AuthCardBody,
   AuthCardTop,
   AuthCardFooter,
   AuthFootnote,
   InputGroup,
} from '../../styles/auth/AuthLayout';

import { Field, Label, Input, HelperText } from '../../styles/ui/Form';
import { PrimaryButton } from '../../styles/ui/Button';
import { TextLink } from '../../styles/ui/Link';

export default function Login() {
   const dispatch = useDispatch();
   const location = useLocation();
   const navigate = useNavigate();
   const prevPath = location.state?.prevPath || '/main';

   const isLoading = useSelector((state) => state.auth.isLoading);
   const isLoggedIn = useSelector((state) => state.auth.isLoggedIn);
   const preAuthId = useSelector((state) => state.auth.preAuthId);

   // Vem preenchido quando o usuário chega da redefinição de senha
   // (`/forgot-password` navega com `state.email`) — evita redigitar o e-mail
   // logo depois de já tê-lo informado duas vezes no fluxo de reset.
   const [email, setEmail] = useState(location.state?.email || '');
   const [password, setPassword] = useState('');
   const [code, setCode] = useState('');

   const [checked, setChecked] = useState(false);

   const APP_VERSION = '1.3.0 - 22/01/2026';

   useEffect(() => {
      setChecked(true);
   }, []);

   if (!checked) return <Loading isLoading />;
   if (isLoggedIn) return <Navigate to="/" replace />;

   const handleSubmit = (e) => {
      e.preventDefault();
      let formErrors = false;

      // ETAPA 2: verificar código
      if (preAuthId) {
         const clean = String(code || '').replace(/\D/g, '');

         if (clean.length < 4 || clean.length > 8) {
            toast.error('Código inválido.');
            return;
         }

         dispatch(
            actions.auth2FAVerifyRequest({
               preAuthId,
               code: clean,
               prevPath,
               navigate,
            })
         );
         return;
      }

      // ETAPA 1: login normal
      if (!isEmail(email)) {
         formErrors = true;
         toast.error('O email precisa ser válido.');
      }

      if (password.length < 6 || password.length > 50) {
         formErrors = true;
         toast.error('Senha inválida.');
      }

      if (formErrors) return;

      dispatch(actions.loginRequest({ email, password, prevPath, navigate }));
   };

   return (
      <AuthPage>
         <AuthContainer $size="lg">
            <Loading isLoading={isLoading} />

            <AuthGrid>
               <AuthVisual $image={loginVisual}>
                  <h2 style={{ margin: 0, fontSize: 18 }}>Bem-vindo</h2>
                  <p style={{ marginTop: 8, opacity: 0.85, lineHeight: 1.5 }}>
                     Acesse com seu email e senha para entrar no sistema.
                  </p>
               </AuthVisual>

               <AuthCard>
                  <AuthCardTop>
                     <AuthBrand>
                        <AuthLogo src={logoMyshop} alt="MyShop" />
                     </AuthBrand>
                  </AuthCardTop>

                  <AuthCardBody>
                     <AuthTitle>Faça seu login</AuthTitle>
                     <AuthSubtitle>
                        Use suas credenciais de acesso.
                     </AuthSubtitle>

                     <AuthForm onSubmit={handleSubmit}>
                        {!preAuthId ? (
                           <>
                              <Field>
                                 <Label>Email</Label>
                                 <Input
                                    type="email"
                                    autoComplete="email"
                                    placeholder="Digite seu email de acesso"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    disabled={isLoading}
                                 />
                              </Field>

                              <Field>
                                 <Label>Senha</Label>
                                 <Input
                                    type="password"
                                    autoComplete="current-password"
                                    placeholder="Digite sua senha"
                                    value={password}
                                    onChange={(e) =>
                                       setPassword(e.target.value)
                                    }
                                    disabled={isLoading}
                                 />
                                 <HelperText>Mínimo 6 caracteres.</HelperText>
                              </Field>

                              <AuthActions>
                                 <PrimaryButton
                                    type="submit"
                                    disabled={isLoading}
                                 >
                                    Entrar
                                 </PrimaryButton>
                              </AuthActions>
                           </>
                        ) : (
                           <>
                              <Field>
                                 <Label>Código de verificação</Label>
                              <InputGroup>
                                 <Input
                                    inputMode="numeric"
                                    placeholder="Digite o código enviado por e-mail"
                                    value={code}
                                    onChange={(e) => setCode(e.target.value)}
                                    disabled={isLoading}
                                    autoFocus
                                 />
                                 <PrimaryButton
                                    type="submit"
                                    disabled={isLoading}
                                 >
                                    Confirmar
                                 </PrimaryButton>
                              </InputGroup>
                                 <HelperText>
                                    Código enviado para {email}.
                                 </HelperText>
                              </Field>

                              <AuthActions>
                                 <TextLink
                                    as="button"
                                    type="button"
                                    disabled={isLoading}
                                    onClick={() => {
                                       setCode('');
                                       dispatch(
                                          actions.auth2FAResendRequest({
                                             email,
                                             password,
                                          })
                                       );
                                    }}
                                 >
                                    Reenviar código
                                 </TextLink>
                                 <TextLink
                                    as="button"
                                    type="button"
                                    disabled={isLoading}
                                    onClick={() => {
                                       dispatch(actions.auth2FAClear());
                                       setCode('');
                                    }}
                                 >
                                    Voltar
                                 </TextLink>
                              </AuthActions>
                           </>
                        )}
                     </AuthForm>
                  </AuthCardBody>

                  <AuthCardFooter>
                     <AuthFootnote>
                        {/* Apontava para `/forgot`, rota que nunca existiu — e
                            como <a href> ainda recarregava o app inteiro. */}
                        <TextLink to="/forgot-password">
                           Esqueceu a senha?
                        </TextLink>
                        <span>v{APP_VERSION}</span>
                     </AuthFootnote>
                  </AuthCardFooter>
               </AuthCard>
            </AuthGrid>
         </AuthContainer>
      </AuthPage>
   );
}
